/**
 * Options Page Entry Point
 * Handles settings and preferences for the Lot Lingo portal.
 */

import * as config from './modules/config';
import { validateConfig } from '../config/validate';
import { createLogger } from '../shared/logger';
import {
  loadStoredAuth,
  loadSettings,
  ensureSettings,
  saveSettings as saveSettingsToStorage,
  state,
} from './modules/state';
import { exchangePkceAuth, getUserProfile, upsertUserProfile } from './modules/auth';
import { displayCreditBalanceInOptions } from './modules/credit';
import {
  sanitizePercentage,
  sanitizeCurrency,
  formatPercentageInput,
  formatCurrencyInput,
} from './modules/formatting';
import { normalizeHomeZipPostal } from './modules/utils';
import { applyServerSettings, toServerSettings } from './modules/settingsSync';
import { showToast } from './modules/ui/toasts';

// Import theme toggle for dark/light mode
import './theme-toggle';

const log = createLogger('portal:options');

validateConfig();

interface OptionsElements {
  loading: HTMLElement | null;
  authRequired: HTMLElement | null;
  content: HTMLElement | null;
  profileCompany: HTMLInputElement | null;
  settingsTz: HTMLSelectElement | null;
  settingsZip: HTMLInputElement | null;
  settingsDistanceUnits: HTMLSelectElement | null;
  settingsUnitsAutoIndicator: HTMLElement | null;
  settingsTaxRate: HTMLInputElement | null;
  settingsBidderPremium: HTMLInputElement | null;
  settingsSellerCosts: HTMLInputElement | null;
  settingsFixedLotCost: HTMLInputElement | null;
  settingsTargetMargin: HTMLInputElement | null;
  settingsBudgetCap: HTMLInputElement | null;
  settingsEnableNotifications: HTMLInputElement | null;
  settingsNotifyLeadMinutes: HTMLInputElement | null;
  settingsNotificationsPermissionStatus: HTMLElement | null;
  saveButton: HTMLButtonElement | null;
  cancelButton: HTMLElement | null;
  creditSection: HTMLElement | null;
  buyCredits: HTMLButtonElement | null;
  manageSubscription: HTMLButtonElement | null;
}

function getElements(): OptionsElements {
  return {
    loading: document.getElementById('options-loading'),
    authRequired: document.getElementById('options-auth-required'),
    content: document.getElementById('options-content'),
    profileCompany: document.getElementById('profile-company') as HTMLInputElement | null,
    settingsTz: document.getElementById('settings-timezone') as HTMLSelectElement | null,
    settingsZip: document.getElementById('settings-zip') as HTMLInputElement | null,
    settingsDistanceUnits: document.getElementById(
      'settings-distance-units'
    ) as HTMLSelectElement | null,
    settingsUnitsAutoIndicator: document.getElementById('settings-units-auto-indicator'),
    settingsTaxRate: document.getElementById('settings-tax-rate') as HTMLInputElement | null,
    settingsBidderPremium: document.getElementById(
      'settings-bidder-premium'
    ) as HTMLInputElement | null,
    settingsSellerCosts: document.getElementById(
      'settings-seller-costs'
    ) as HTMLInputElement | null,
    settingsFixedLotCost: document.getElementById(
      'settings-fixed-lot-cost'
    ) as HTMLInputElement | null,
    settingsTargetMargin: document.getElementById(
      'settings-target-margin'
    ) as HTMLInputElement | null,
    settingsBudgetCap: document.getElementById('settings-budget-cap') as HTMLInputElement | null,
    settingsEnableNotifications: document.getElementById(
      'settings-enable-notifications'
    ) as HTMLInputElement | null,
    settingsNotifyLeadMinutes: document.getElementById(
      'settings-notify-lead-minutes'
    ) as HTMLInputElement | null,
    settingsNotificationsPermissionStatus: document.getElementById(
      'settings-notifications-permission-status'
    ),
    saveButton: document.getElementById('save-settings') as HTMLButtonElement | null,
    cancelButton: document.getElementById('cancel-settings'),
    creditSection: document.getElementById('options-credit-section'),
    buyCredits: document.getElementById('options-buy-credits') as HTMLButtonElement | null,
    manageSubscription: document.getElementById(
      'options-manage-subscription'
    ) as HTMLButtonElement | null,
  };
}

function populateTimeZones(selectEl: HTMLSelectElement | null): void {
  if (!selectEl) return;

  let zones: string[] = [];
  try {
    if (Intl && typeof (Intl as any).supportedValuesOf === 'function') {
      zones = (Intl as any).supportedValuesOf('timeZone') || [];
    }
  } catch {
    zones = [];
  }

  if (!Array.isArray(zones) || zones.length === 0) {
    zones = [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Europe/London',
    ];
  }

  if (selectEl.options.length === 0) {
    zones.forEach(z => {
      const opt = document.createElement('option');
      opt.value = z;
      opt.textContent = z;
      selectEl.appendChild(opt);
    });
  }
}

function setupUnitsHintRefresh(els: OptionsElements): void {
  function refreshUnitsHint() {
    if (!els.settingsUnitsAutoIndicator) return;

    const sel = els.settingsDistanceUnits?.value || 'auto';

    if (sel !== 'auto') {
      els.settingsUnitsAutoIndicator.dataset.visible = 'false';
      return;
    }

    const v = els.settingsZip?.value || '';
    const isCA = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(v.trim());
    els.settingsUnitsAutoIndicator.textContent = isCA ? 'Auto: km' : 'Auto: mi';
    els.settingsUnitsAutoIndicator.dataset.visible = 'true';
  }

  refreshUnitsHint();

  if (els.settingsZip) {
    els.settingsZip.addEventListener('input', refreshUnitsHint);
  }
  if (els.settingsDistanceUnits) {
    els.settingsDistanceUnits.addEventListener('change', refreshUnitsHint);
  }
}

function populateForm(els: OptionsElements, profile: any | null): void {
  const settings = loadSettings();

  // Apply profile settings to local settings (server values take precedence)
  // This ensures settings persist across devices/sessions
  if (profile) {
    if (typeof profile.timeZone === 'string' && profile.timeZone) {
      settings.timeZone = profile.timeZone;
    }
    if (typeof profile.homePostalCode === 'string' && profile.homePostalCode) {
      settings.zipCode = profile.homePostalCode;
    }
    // Server-backed decision settings (fees, target margin, notifications)
    if (profile.settings && typeof profile.settings === 'object') {
      applyServerSettings(settings, profile.settings);
    }
    // Save merged settings to localStorage for offline access
    saveSettingsToStorage();
  }

  // Populate timezone
  populateTimeZones(els.settingsTz);
  if (els.settingsTz && settings.timeZone) {
    els.settingsTz.value = settings.timeZone;
  }

  // Populate ZIP (prefer profile.homePostalCode if available)
  if (els.settingsZip) {
    els.settingsZip.value = settings.zipCode || '';
  }

  // Populate distance units
  if (els.settingsDistanceUnits) {
    let du = settings.distanceUnits || 'auto';
    if (du !== 'mi' && du !== 'km') du = 'auto';
    els.settingsDistanceUnits.value = du;
  }

  setupUnitsHintRefresh(els);

  // Populate numeric fields
  if (els.settingsTaxRate) {
    els.settingsTaxRate.value = formatPercentageInput(sanitizePercentage(settings.taxRate, 0));
  }
  if (els.settingsBidderPremium) {
    els.settingsBidderPremium.value = formatPercentageInput(
      sanitizePercentage(settings.bidderPremium, 15)
    );
  }
  if (els.settingsSellerCosts) {
    els.settingsSellerCosts.value = formatPercentageInput(
      sanitizePercentage(settings.sellerCosts, 14)
    );
  }
  if (els.settingsFixedLotCost) {
    els.settingsFixedLotCost.value = formatCurrencyInput(
      sanitizeCurrency(settings.fixedLotCost, 0)
    );
  }
  if (els.settingsTargetMargin) {
    els.settingsTargetMargin.value = formatPercentageInput(
      sanitizePercentage(settings.targetMarginPct, 30)
    );
  }
  if (els.settingsBudgetCap) {
    const cap =
      typeof settings.budgetCap === 'number' &&
      Number.isFinite(settings.budgetCap) &&
      settings.budgetCap > 0
        ? settings.budgetCap
        : null;
    els.settingsBudgetCap.value = cap != null ? String(cap) : '';
  }

  // Notifications
  if (els.settingsEnableNotifications) {
    els.settingsEnableNotifications.checked = !!settings.enableNotifications;
  }
  if (els.settingsNotifyLeadMinutes) {
    const leadList: number[] = Array.isArray(settings.notifyLeadMinutes)
      ? settings.notifyLeadMinutes.filter((m: number) => Number.isFinite(m)).map(Number)
      : [60, 30, 10];
    els.settingsNotifyLeadMinutes.value = leadList.join(',');
  }
  if (els.settingsNotificationsPermissionStatus && typeof Notification !== 'undefined') {
    els.settingsNotificationsPermissionStatus.textContent = Notification.permission;
  }

  // Profile data (company and distanceUnits)
  if (profile) {
    if (els.profileCompany && typeof profile.company === 'string') {
      els.profileCompany.value = profile.company;
    }
    if (els.settingsDistanceUnits && typeof profile.distanceUnits === 'string') {
      const du = profile.distanceUnits.toLowerCase();
      if (du === 'mi' || du === 'km' || du === 'auto') {
        els.settingsDistanceUnits.value = du;
      }
    }
  }
}

function parseNotifyLeadMinutes(raw: string): number[] {
  const parsed = raw
    .split(/[\,\s]+/)
    .map(v => Number(v.trim()))
    .filter(n => Number.isFinite(n) && n > 0 && n < 24 * 60);

  const unique: number[] = [];
  for (const m of parsed) {
    if (!unique.includes(m)) unique.push(m);
  }
  unique.sort((a, b) => b - a);
  return unique.length ? unique : [60, 30, 10];
}

async function saveSettings(
  els: OptionsElements,
  cfg: config.Config | null,
  mode: string
): Promise<void> {
  const settings = ensureSettings();
  const oldZipCode = settings.zipCode;

  // Timezone
  if (els.settingsTz) {
    settings.timeZone = els.settingsTz.value || undefined;
  }

  // ZIP code
  if (els.settingsZip) {
    const normalized = normalizeHomeZipPostal((els.settingsZip.value || '').trim());
    settings.zipCode = normalized;
    els.settingsZip.value = settings.zipCode;

    // Clear distance cache if zip changed
    if (oldZipCode !== settings.zipCode) {
      const windowWithCache = window as Window & { __distanceCache?: Record<string, unknown> };
      windowWithCache.__distanceCache = {};
    }
  }

  // Distance units
  if (els.settingsDistanceUnits) {
    const duVal = (els.settingsDistanceUnits.value || 'auto').toLowerCase();
    settings.distanceUnits = duVal === 'mi' || duVal === 'km' ? duVal : 'auto';
  }

  // Numeric fields
  if (els.settingsTaxRate) {
    settings.taxRate = sanitizePercentage(els.settingsTaxRate.value, 0);
    els.settingsTaxRate.value = formatPercentageInput(settings.taxRate);
  }
  if (els.settingsBidderPremium) {
    settings.bidderPremium = sanitizePercentage(els.settingsBidderPremium.value, 15);
    els.settingsBidderPremium.value = formatPercentageInput(settings.bidderPremium);
  }
  if (els.settingsSellerCosts) {
    settings.sellerCosts = sanitizePercentage(els.settingsSellerCosts.value, 14);
    els.settingsSellerCosts.value = formatPercentageInput(settings.sellerCosts);
  }
  if (els.settingsFixedLotCost) {
    settings.fixedLotCost = sanitizeCurrency(els.settingsFixedLotCost.value, 0);
    els.settingsFixedLotCost.value = formatCurrencyInput(settings.fixedLotCost);
  }
  if (els.settingsTargetMargin) {
    settings.targetMarginPct = sanitizePercentage(els.settingsTargetMargin.value, 30);
    els.settingsTargetMargin.value = formatPercentageInput(settings.targetMarginPct);
  }
  if (els.settingsBudgetCap) {
    const raw = (els.settingsBudgetCap.value || '').trim();
    if (!raw) {
      settings.budgetCap = null;
    } else {
      const cap = sanitizeCurrency(raw, 0);
      settings.budgetCap = cap > 0 ? cap : null;
      els.settingsBudgetCap.value = settings.budgetCap != null ? String(settings.budgetCap) : '';
    }
  }

  // Notifications
  if (els.settingsEnableNotifications) {
    settings.enableNotifications = els.settingsEnableNotifications.checked;
    if (
      settings.enableNotifications &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default'
    ) {
      try {
        await Notification.requestPermission();
        if (els.settingsNotificationsPermissionStatus) {
          els.settingsNotificationsPermissionStatus.textContent = Notification.permission;
        }
      } catch {
        // Silently fail
      }
    }
  }
  if (els.settingsNotifyLeadMinutes) {
    settings.notifyLeadMinutes = parseNotifyLeadMinutes(els.settingsNotifyLeadMinutes.value || '');
    els.settingsNotifyLeadMinutes.value = settings.notifyLeadMinutes.join(',');
  }

  // Sync profile to server if authenticated
  if (mode === 'auth0' && cfg) {
    try {
      const profileUpdate: Record<string, any> = {};
      if (els.profileCompany) {
        profileUpdate.company = els.profileCompany.value || '';
      }
      if (settings.timeZone) {
        profileUpdate.timeZone = settings.timeZone;
      }
      if (settings.zipCode) {
        profileUpdate.homePostalCode = settings.zipCode;
      }
      // Server-backed decision settings (fees, target margin, notifications)
      profileUpdate.settings = toServerSettings(settings);
      // Note: distanceUnits is stored locally only, not synced to server profile
      await upsertUserProfile(cfg, profileUpdate);
    } catch (err) {
      log.warn('Failed to sync profile:', err);
    }
  }

  saveSettingsToStorage();
  showToast('Settings saved');
}

(async function initOptionsPage() {
  log.info('[options] Initializing options page');

  const cfg = await config.loadConfig();
  if (!cfg) {
    log.warn('[options] No config loaded');
    return;
  }

  const els = getElements();

  // Check auth state
  const hasAuth0 = !!(
    cfg.auth0Domain &&
    cfg.auth0ClientId &&
    cfg.auth0ClientId !== 'PLACEHOLDER_CLIENT_ID' &&
    !cfg.auth0ClientId.includes('PLACEHOLDER')
  );

  let mode = 'local';
  let profile = null;

  // Try to restore auth from storage
  const storedAuth = loadStoredAuth();
  if (storedAuth?.mode === 'auth0' && storedAuth.extAccessToken) {
    mode = 'auth0';
  }

  // Try Auth0 PKCE auth if Auth0 is configured
  if (hasAuth0 && mode !== 'auth0') {
    try {
      const swaResult = await exchangePkceAuth(cfg);
      if (swaResult?.accessToken) {
        // Save the token to state so getUserProfile can use it
        state.extAccessToken = swaResult.accessToken;
        state.extRefreshToken = swaResult.refreshToken || null;
        state.extExpiresAt = swaResult.expiresIn ? Date.now() + swaResult.expiresIn * 1000 : 0;
        mode = 'auth0';
      }
    } catch (err) {
      log.warn('[options] Auth0 PKCE auth failed:', err);
    }
  }

  // Load profile if authenticated
  if (mode === 'auth0') {
    try {
      profile = await getUserProfile(cfg);
    } catch (err) {
      log.warn('[options] Failed to load profile:', err);
    }
  }

  // Hide loading, show content
  if (els.loading) els.loading.hidden = true;

  // Show content (allow local mode too for settings)
  if (els.content) els.content.hidden = false;

  // Show/hide credit section based on auth mode
  if (els.creditSection) {
    els.creditSection.hidden = mode !== 'auth0';
    if (mode === 'auth0') {
      void displayCreditBalanceInOptions();
    }
  }

  // Populate the form
  populateForm(els, profile);

  // Save button handler
  if (els.saveButton) {
    els.saveButton.addEventListener('click', async () => {
      els.saveButton!.disabled = true;
      els.saveButton!.textContent = 'Saving...';
      try {
        await saveSettings(els, cfg, mode);
        // Redirect back to portal after save
        window.location.href = '/portal';
      } catch (err) {
        log.error('[options] Failed to save settings:', err);
        showToast('Failed to save settings', 'error');
        els.saveButton!.disabled = false;
        els.saveButton!.textContent = 'Save';
      }
    });
  }

  log.info('[options] Options page initialized');
})().catch(err => {
  log.error('[options] Options page initialization failed:', err);
});
